<template>
  <VOverlay :value="overlayIsActive" :z-index="201" @click="clickOutside" />
  <div v-if="modalIsActive" :class="classes">
    <div class="v-modal__inner" :style="styles">
      <template v-if="$slots.headerTitle || $slots.headerAction">
        <div class="v-modal__header">
          <p class="v-modal__header__title">
            <slot name="headerTitle" />
          </p>
          <slot name="headerAction" />
        </div>
      </template>

      <div class="v-modal__content">
        <slot />
      </div>

      <template v-if="$slots.footerAction">
        <div class="v-modal__footer">
          <slot name="footerAction" />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import VOverlay from '@/components/atoms/VOverlay/VOverlay.vue';

interface Props {
  value: boolean;
  absolute?: boolean;
  fullscreen?: boolean;
  persistent?: boolean;
  width?: string;
  maxWidth?: string;
  backgroundColor?: string;
}

const props = withDefaults(defineProps<Props>(), {
  value: false,
  absolute: false,
  fullscreen: false,
  persistent: false,
  width: '320px',
  maxWidth: '90vw',
  backgroundColor: 'var(--bg-base)',
});

const emit = defineEmits<{
  (e: 'update:value', value: boolean): void;
  (e: 'click:outside', value: MouseEvent): void;
}>();

const modalIsActive = computed(() => props.value);
const overlayIsActive = computed(() => props.value && !props.fullscreen);
const classes = computed(() => ({
  'v-modal': true,
  'v-modal--fullscreen': props.fullscreen,
}));
const styles = computed(() => ({
  ...(props.fullscreen ? {} : { width: props.width }),
  ...(props.fullscreen ? {} : { maxWidth: props.maxWidth }),
  ...(props.backgroundColor ? { backgroundColor: props.backgroundColor } : {}),
}));

/**
 * 枠外をクリックするとダイアログを閉じる
 */
const clickOutside = (e: MouseEvent) => {
  if (props.persistent) return false;
  emit('click:outside', e);
  emit('update:value', false);
};

/**
 * ダイアログ展開時の背景スクロールの制御
 */
const scrollTop = ref(0);
const _onOpenModal = () => {
  const $body = document.querySelector('body');
  if (!$body) return;
  scrollTop.value = window.scrollY;
  $body.style.position = 'fixed';
  $body.style.top = `-${scrollTop.value}px`;
  $body.style.left = '0';
  $body.style.right = '0';
};
const _onCloseModal = () => {
  const $body = document.querySelector('body');
  if (!$body) return;
  $body.style.position = '';
  $body.style.top = '';
  window.scrollTo({ top: scrollTop.value });
};
watch(
  () => props.value,
  (open) => {
    if (open) _onOpenModal();
    else _onCloseModal();
  }
);
onBeforeUnmount(_onCloseModal);
</script>

<style scoped lang="scss">
$modal-border-radius: $border-radius-root !default;
$modal-elevation: 24 !default;
$modal-margin: 24px !default;
$modal-max-height: 90% !default;

.v-modal {
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(100% - $tab-bar-height);
  left: 0;
  pointer-events: none;
  position: fixed;
  top: 0;
  transition: 0.2s map-get($transition, 'fast-in-fast-out'), z-index 1ms;
  width: 100%;
  z-index: 202;
  outline: none;

  &__header,
  &__content,
  &__footer {
    padding-right: 20px;
    padding-left: 20px;

    > ::v-deep(*) {
      :last-child {
        margin-bottom: 0;
      }
    }
  }

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-right: -5px;
    margin-bottom: -4px;
    padding-top: 24px;
    text-align: right;

    &__title {
      line-height: 1.25;
      font-size: 22px;
      font-weight: 300;
      color: $color-grey-light2;
    }
  }

  &__content {
    padding-top: 24px;
    padding-bottom: 24px;
  }

  &__footer {
    padding-top: 24px;
    padding-bottom: 24px;
    background-color: $bg-base;
    border-top: 1px $bg-dark solid;
    text-align: center;
  }

  &__inner {
    color: #fff;
    background: $bg-base;
    border-radius: $modal-border-radius;
    margin: $modal-margin;
    overflow-y: auto;
    overflow-x: hidden;
    pointer-events: auto;
    transition: 0.3s map-get($transition, 'fast-in-fast-out');
    width: 100%;
    z-index: inherit;
    overflow-wrap: break-word;

    &:not(.v-modal--fullscreen) {
      @include elevation($modal-elevation);

      max-height: $modal-max-height;
    }
  }

  &--fullscreen {
    border-radius: 0;
    margin: 0;
    height: 100%;
    position: fixed;
    overflow-y: auto;
    top: 0;
    left: 0;
    padding: 0 0 $tab-bar-height;

    > ::v-deep(div) {
      min-height: 100%;
      min-width: 100%;
      margin: 0 !important;
      padding: 0 !important;
    }

    .v-modal__content {
      margin: 0;
      padding: 0;
    }
  }
}
</style>
